import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

// Always fetch fresh — covers change rarely but when they do (e.g. cover swap
// in the admin CMS), readers and the editor expect the change to land on the
// next homepage refresh, not after a session-long module-level cache expires.
function loadCovers() {
  return api.get("/articles/cover")
    .then(({ data }) => data || [])
    .catch(() => []);
}

function CoverBlock({ cover, theme = "light", kicker }) {
  if (!cover) return null;
  const dark = theme === "dark";
  // Backdrop colour behind the magazine cover artwork.
  const bgClass = dark ? "bg-[#0B0B0C] text-white" : "bg-[#EFEAE0] text-[#111]";
  const accent = dark ? "text-[var(--hinton-gold)]" : "text-[var(--hinton-red)]";

  return (
    <section className={`relative overflow-hidden ${bgClass}`} data-testid={`cover-${cover.cover_slot || "extra"}`}>
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch">
        {/* Magazine cover — full image, never cropped */}
        <Link to={`/article/${cover.slug}`} className="lg:col-span-6 flex items-center justify-center p-6 md:p-10 lg:p-14 group">
          <div className="relative max-w-[460px] w-full" data-testid={`cover-image-${cover.cover_slot}`}>
            <div className={`absolute -inset-3 sm:-inset-5 ${dark ? "bg-white/5" : "bg-black/5"}`} />
            <img
              src={cover.cover_artwork || cover.cover_image}
              alt={`${cover.cover_issue} cover · ${cover.cover_star || cover.title}`}
              className="relative w-full h-auto shadow-2xl transition-transform duration-700 group-hover:-translate-y-1"
            />
          </div>
        </Link>

        {/* Copy */}
        <div className="lg:col-span-6 flex flex-col justify-center p-8 sm:p-12 lg:p-16">
          <div className={`overline mb-5 ${accent}`}>{kicker}</div>
          <div className={`font-mono-ed text-xs uppercase tracking-[0.32em] mb-3 ${dark ? "text-neutral-400" : "text-neutral-600"}`}>
            {cover.cover_issue}
          </div>
          <h2 className="font-display text-5xl sm:text-6xl md:text-7xl lg:text-[88px] font-black tracking-[-0.045em] leading-[0.86]">
            {cover.cover_star || cover.title.split(":")[0]}
          </h2>
          <p className={`mt-6 max-w-xl font-serif-ed italic text-lg md:text-xl leading-relaxed ${dark ? "text-neutral-300" : "text-neutral-700"}`}>
            {cover.excerpt}
          </p>
          <div className="mt-10 flex items-center gap-6">
            <Link
              to={`/article/${cover.slug}`}
              className={`inline-flex items-center gap-3 px-7 py-4 text-xs uppercase tracking-[0.22em] font-bold transition-colors ${
                dark
                  ? "bg-white text-black hover:bg-[var(--hinton-red)] hover:text-white"
                  : "bg-black text-white hover:bg-[var(--hinton-red)] hover:text-white"
              }`}
              data-testid={`cover-cta-${cover.cover_slot}`}
            >
              Read the cover story <ArrowUpRight size={14} />
            </Link>
            {cover.author && cover.author !== "Hinton Editorial" && (
              <div className={`font-mono-ed text-[11px] uppercase tracking-[0.22em] ${dark ? "text-neutral-500" : "text-neutral-500"}`}>
                By {cover.author}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

export function SeasonalCover() {
  const [cover, setCover] = useState(null);
  useEffect(() => {
    loadCovers().then((list) => setCover(list.find((c) => c.cover_slot === "seasonal") || null));
  }, []);
  return <CoverBlock cover={cover} theme="light" kicker="The Seasonal Cover" />;
}

export function MonthlyCover() {
  const [cover, setCover] = useState(null);
  useEffect(() => {
    loadCovers().then((list) => setCover(list.find((c) => c.cover_slot === "monthly") || null));
  }, []);
  return <CoverBlock cover={cover} theme="dark" kicker="The Monthly Cover" />;
}
